import { z } from 'zod';
import type {
  activitiesActivityFlowProjectPathObj,
  contractorPaymentIdProjectPathObj,
  legalBasesProjectPathObj,
  paymentAmountBreakdownPathObj,
  paymentGroupProjectPathObj,
  planProjectPathObj,
  projectPathObj,
  rootActivityPathObj,
} from '../models/pinpoint-path';

export const SsBasicInformationScenePinpointFieldEnum = z.enum([
  'ss.snapshot',
  'ss.organization-in-charge',
  'ss.people-in-charge',
  'ss.business-manager',
  'ss.other-organization-in-charge',
  'ss.related-organizations',
  'ss.project-number',
  'ss.project-group',
  'ss.sheet-type',
  'ss.independent-administrative-agency',
  'ss.fiscal-year',
  'ss.project-category',
  'ss.period',
  'ss.related-projects',
  'ss.main-cost',
  'ss.segment-unit-concept',
  'ss.project-purpose',
  'ss.project-task',
  'ss.overview',
  'ss.overview-url',
  'ss.legal-bases-title',
  'ss.legal-bases',
  'ss.plan-title',
  'ss.plan',
  'ss.old-project-number',
  'ss.project-order-number',
  'ss.remark',
]);

export const SsDetailedBreakdownScenePinpointFieldEnum = z.enum([
  'ss.budget-amounts-table-title',
  'ss.budget-amounts-table',
  'ss.other-special-notes',
]);

// 「アクティビティ／活動・成果目標等」タブ
export const SsActivitiesScenePinpointFieldEnum = z.enum([
  'ss.activity-title',
  'ss.activity',
  'ss.activities-and-outcomes-title',
]);

export const SsActivityFlowScenePinpointFieldEnum = z.enum([
  'ss.indicator-title',
  'ss.indicator-description',
  'ss.activity-and-result-target',
  'ss.achievement-for-qualitative-outcome',
  'ss.indicator-setting-reason',
  'ss.reason-for-setting-qualitative-outcome',
  'ss.reason-why-outcomes-cannot-be-multiple',
  'ss.connection-to-next-outcome',
]);

export const SsInspectionEvaluationScenePinpointFieldEnum = z.enum([
  'ss.inspection-result',
  'ss.evaluation',
  'ss.direction-of-improvement',
]);

export const SsPaymentScenePinpointFieldEnum = z.enum([
  'ss.payment-title',
  'ss.payment-group-row',
  'ss.payment-contractor-title',
  'ss.payment-contractor-row',
  'ss.payment-amount-breakdown-title',
  'ss.payment-amount-breakdown-row',
]);

export const SsProjectRelatedMaterialScenePinpointFieldEnum = z.enum([
  'ss.related-material-image',
  'ss.related-material-remark',
]);

export const SsPinpointFieldEnum = z.enum([
  ...SsBasicInformationScenePinpointFieldEnum.options,
  ...SsDetailedBreakdownScenePinpointFieldEnum.options,
  ...SsProjectRelatedMaterialScenePinpointFieldEnum.options,
  ...SsInspectionEvaluationScenePinpointFieldEnum.options,
  ...SsPaymentScenePinpointFieldEnum.options,
  ...SsActivityFlowScenePinpointFieldEnum.options,
  ...SsActivitiesScenePinpointFieldEnum.options,
]);

export const SsPinpointMultipleFieldEnum = z.enum([
  SsBasicInformationScenePinpointFieldEnum.Enum['ss.legal-bases'],
  SsBasicInformationScenePinpointFieldEnum.Enum['ss.plan'],
  SsPaymentScenePinpointFieldEnum.Enum['ss.payment-contractor-row'],
  SsPaymentScenePinpointFieldEnum.Enum['ss.payment-group-row'],
  SsActivitiesScenePinpointFieldEnum.Enum['ss.activity'],
  ...SsActivityFlowScenePinpointFieldEnum.options,
  SsPaymentScenePinpointFieldEnum.Enum['ss.payment-amount-breakdown-row'],
]);

type SsPinpointMultipleFieldEnumFieldEnumType =
  typeof SsPinpointMultipleFieldEnum.Enum;

type ProjectPinpointMappingType = {
  path: z.infer<typeof projectPathObj>;
  field: Exclude<
    | z.infer<typeof SsBasicInformationScenePinpointFieldEnum>
    | z.infer<typeof SsDetailedBreakdownScenePinpointFieldEnum>
    | z.infer<typeof SsProjectRelatedMaterialScenePinpointFieldEnum>
    | z.infer<typeof SsInspectionEvaluationScenePinpointFieldEnum>
    | z.infer<typeof SsPaymentScenePinpointFieldEnum>
    | z.infer<typeof SsActivitiesScenePinpointFieldEnum>,
    z.infer<typeof SsPinpointMultipleFieldEnum>
  >;
};

export type SsActivityFlowPinpointMappingType = {
  path: z.infer<typeof activitiesActivityFlowProjectPathObj>;
  field: Exclude<
    z.infer<typeof SsActivityFlowScenePinpointFieldEnum>,
    'ss.activity'
  >;
};

export type SsActivity = {
  path: z.infer<typeof rootActivityPathObj>;
  field: (typeof SsActivitiesScenePinpointFieldEnum.Enum)['ss.activity'];
};

export type SsMultiplePinpointMappingType =
  | {
      path: z.infer<typeof legalBasesProjectPathObj>;
      field: SsPinpointMultipleFieldEnumFieldEnumType['ss.legal-bases'];
    }
  | {
      path: z.infer<typeof planProjectPathObj>;
      field: SsPinpointMultipleFieldEnumFieldEnumType['ss.plan'];
    }
  | {
      path: z.infer<typeof contractorPaymentIdProjectPathObj>;
      field: (typeof SsPaymentScenePinpointFieldEnum.Enum)['ss.payment-contractor-row'];
    }
  | {
      path: z.infer<typeof paymentGroupProjectPathObj>;
      field: (typeof SsPaymentScenePinpointFieldEnum.Enum)['ss.payment-group-row'];
    }
  | {
      path: z.infer<typeof paymentAmountBreakdownPathObj>;
      field: (typeof SsPaymentScenePinpointFieldEnum.Enum)['ss.payment-amount-breakdown-row'];
    }
  | SsActivity
  | SsActivityFlowPinpointMappingType;

export type SsPinpointMappingType =
  | ProjectPinpointMappingType
  | SsMultiplePinpointMappingType;
